import { useEffect, useState } from "react";
import { UseFieldArrayAppend, useForm, UseFormSetValue } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { logger } from "@/services/Logger";
import {
  ItemOptionPrice,
  MenuFormData,
  MenuItem,
  MenuItemFormData,
  MenuItemOption,
} from "@/redux/services/menu/menu.type";
import {
  cleanerError,
  convertPriceFromCent,
  convertPriceToCent,
} from "@/lib/utils";
import {
  useCreateOrUpdateMenuItemMutation,
  useLazyGetMenuItemByIdQuery,
  useUpdateProductMutation,
} from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";
import { useUploadMedia } from "@/lib/useUploadMedia";
setLocale(fr);

// Yup validation schema for a menu item form
export const menuItemSchema = yup.object().shape({
  title: yup.string().required().label("Titre"),
  description: yup.string(),
  sortOrder: yup.string().required().label("Position"),
  basePrice: yup.string().required().label("Prix"),
  image: yup.mixed().label("Image"),
  itemOptions: yup.array().of(
    yup
      .object()
      .shape({
        itemOptionPrices: yup
          .array()
          .of(
            yup.object().shape({
              priceModifier: yup
                .number()
                .required()
                .label("Modificateur de prix"),
            })
          )
          .when("isRequired", {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.notRequired(),
          }),
      })
      .required()
  ),
});

// Props type for the useCrudMenuItem hook
type UseCrudMenuItemProps = {
  appendMenuItem: UseFieldArrayAppend<
    MenuFormData,
    `menuSections.${number}.menuItems`
  >;
  close: () => void;
  item: MenuItemFormData;
  menuItemOptions: any;
  sectionIndex: number;
  menuItemIndex: number;
  setValues: UseFormSetValue<MenuFormData>;
  sectionId?: string;
  menuItemId?: string;
};

const transformItemOptions = (itemOptions: any[]) => {
  return itemOptions?.map((itemOption) => ({
    "@id": itemOption["@id"],
    menuItemOption: `/api/menu_item_options/${itemOption?.menuItemOption?.id}`,
    itemOptionPrices: itemOption?.itemOptionPrices?.map(
      (itemOptionPrice: ItemOptionPrice) => ({
        "@id": itemOptionPrice["@id"],
        priceModifier: convertPriceFromCent(itemOptionPrice?.priceModifier),
        menuItemOptionValue: `/api/menu_item_option_values/${itemOptionPrice?.menuItemOptionValue?.id}`,
      })
    ),
  }));
};

// Hook for handling the CRUD logic for a menu item
export const useCrudMenuItem = (props: UseCrudMenuItemProps) => {
  const {
    appendMenuItem,
    close,
    item,
    sectionIndex,
    menuItemIndex,
    setValues,
    sectionId,
    menuItemId,
  } = props;

  // State variables
  const [selectedMenuItemOptions, setSelectedMenuItemOptions] = useState<any>();
  const [
    selectedMenuItemOptionsWithValues,
    setSelectedMenuItemOptionsWithValues,
  ] = useState<MenuItemOption[]>();
  const [menuItem, setMenuItem] = useState<MenuItem>();

  // API mutation hooks
  const [findMenuItemById, { isLoading: isLoadFetchMenuItem }] =
    useLazyGetMenuItemByIdQuery();
  const [save] = useCreateOrUpdateMenuItemMutation();
  const [updateProduct] = useUpdateProductMutation();
  const { uploadMedia } = useUploadMedia();

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    setValue,
    reset,
  } = useForm<MenuItemFormData>({
    defaultValues: {
      sortOrder: 1,
    },
    // @ts-ignore
    resolver: yupResolver(menuItemSchema, { context: { isRequired: true } }),
  });

  // Effect to clean up errors
  useEffect(() => {
    if (errors) {
      cleanerError(errors, clearErrors);
    }
  }, [errors, clearErrors]);

  const itemOptions = localStorage.getItem("menuItemOptions");

  // Fetch menu item data by ID when menuItemId is provided
  useEffect(() => {
    if (menuItemId) {
      findMenuItemById(menuItemId).then((res) => {
        if ("data" in res) {
          setMenuItem(res.data);
        }
      });
    }
  }, [menuItemId]);

  useEffect(() => {
    if (itemOptions) {
      const selected = selectedMenuItemOptions?.map((item: any) =>
        JSON.parse(itemOptions)?.find(
          (item2: MenuItemOption) => item2?.id === item?.value
        )
      );
      setSelectedMenuItemOptionsWithValues(selected);
    }
  }, [itemOptions, selectedMenuItemOptions]);

  // Populate form fields when the menu item is fetched
  useEffect(() => {
    if (menuItem) {
      setValue("title", menuItem?.title);
      setValue("description", menuItem?.description);
      setValue("sortOrder", Number(menuItem?.sortOrder));
      setValue("basePrice", convertPriceFromCent(menuItem?.basePrice));
      setValue("itemOptions", transformItemOptions(menuItem?.itemOptions));
      const selectedOptions = item?.itemOptions?.map((option: any) => {
        return {
          label: option?.title,
          value: option?.id,
        };
      });
      setSelectedMenuItemOptions(selectedOptions);
    } else if (item) {
      setValue("title", item?.title);
      setValue("description", item?.description);
      setValue("sortOrder", item?.sortOrder);
      setValue("basePrice", item?.basePrice);
      setValue("itemOptions", item?.itemOptions);

      if (itemOptions && !menuItemId) {
        const selectedOptions = item?.itemOptions?.map((option) => {
          const optionId = option?.menuItemOption?.split("/").pop();

          const correspondingOption = JSON.parse(itemOptions).find(
            (item: any) => item.id === parseInt(optionId as string)
          );

          return {
            label: correspondingOption?.title,
            value: correspondingOption?.id,
          };
        });
        setSelectedMenuItemOptions(selectedOptions);
      }
    }
  }, [menuItem]);

  const onSelectMenuItemImage = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.currentTarget.files) {
      const file = e.currentTarget.files[0];
      const media = await uploadMedia(file);
      if (media && "id" in media) {
        setValue("newImageUrl", media?.url);
        setValue(`image`, `/api/media_objects/${media?.id}`);
      }
    }
  };

  const onSelectMenuItemOption = (data: any) => {
    setSelectedMenuItemOptions(data);

    if (menuItem) {
      const selectedItemOptions =
        menuItem.itemOptions?.filter((option: any) =>
          data.some(
            (selectedOption: any) =>
              selectedOption.value === option.menuItemOption.id
          )
        ) || [];

      data.forEach((selectedOption: any) => {
        const exists = selectedItemOptions.some(
          (option: any) => option.menuItemOption.id === selectedOption.value
        );

        if (!exists) {
          selectedItemOptions.push({
            // @ts-ignore
            menuItemOption: {
              id: selectedOption.value,
            },
            itemOptionPrices: [],
          });
        }
      });

      const selectedItemOptionsWithPrices =
        transformItemOptions(selectedItemOptions);
      setValue("itemOptions", selectedItemOptionsWithPrices);
    } else {
      setValue(
        `itemOptions`,
        data?.map((option: any) => {
          return {
            menuItemOption: `/api/menu_item_options/${option?.value}`,
          };
        })
      );
    }
  };

  // handle change price modifier
  const handleChangePriceModifier = (
    e: React.ChangeEvent<HTMLInputElement>,
    menuItemOptionValueId: string,
    menuItemOtionIndex: number,
    menuItemOptionValueIndex: number
  ) => {
    setValue(
      `itemOptions.${menuItemOtionIndex}.itemOptionPrices.${menuItemOptionValueIndex}`,
      {
        priceModifier: parseInt(e.currentTarget.value) ?? 0,
        menuItemOptionValue: `/api/menu_item_option_values/${menuItemOptionValueId}`,
      }
    );
  };

  // Save or update the menu item
  const handleSave = async (data: MenuItemFormData) => {
    const res = await save({
      id: item?.id,
      data: {
        ...data,
        basePrice: convertPriceToCent(data.basePrice),
        sortOrder: Number(data.sortOrder),
        menuSection: `/api/menu_sections/${sectionId}`,
        // convert price modifier to cent
        itemOptions: data?.itemOptions?.map((itemOption) => ({
          ...itemOption,
          itemOptionPrices: itemOption?.itemOptionPrices?.map(
            (itemOptionPrice) => ({
              ...itemOptionPrice,
              priceModifier: convertPriceToCent(itemOptionPrice?.priceModifier),
            })
          ),
        })),
        "@id": `/api/menu_items/${menuItemId}`,
      },
    });
    if ("data" in res) {
      // Identity lives on the dish: editing an existing placement updates its
      // Product so the change shows on every menu the dish is on (SFRESTO-110).
      if (menuItemId && menuItem?.product?.id) {
        await updateProduct({
          id: menuItem.product.id,
          data: {
            title: data.title,
            description: data.description,
            ...(typeof data.image === "string" ? { image: data.image } : {}),
          },
        });
      }
      toast.success(menuItemId ? "Mise à jour réussie" : "Ajouté avec succès");
      reset();
      close();
    } else if ("error" in res) {
      logger.error("useCrudMenuItem", "Error submitting form:", res.error);
      toast.error("Une erreur inconnue s'est produite");
    }
  };

  const onSubmit = async (data: MenuItemFormData) => {
    if (item) {
      setValues(
        `menuSections.${sectionIndex}.menuItems.${menuItemIndex}.title`,
        data.title
      );
      setValues(
        `menuSections.${sectionIndex}.menuItems.${menuItemIndex}.description`,
        data.description
      );
      setValues(
        `menuSections.${sectionIndex}.menuItems.${menuItemIndex}.sortOrder`,
        data.sortOrder
      );
      setValues(
        `menuSections.${sectionIndex}.menuItems.${menuItemIndex}.basePrice`,
        data.basePrice
      );
      setValues(
        `menuSections.${sectionIndex}.menuItems.${menuItemIndex}.image`,
        data.image
      );
      setValues(
        `menuSections.${sectionIndex}.menuItems.${menuItemIndex}.itemOptions`,
        data.itemOptions
      );
    } else {
      appendMenuItem(data);
    }
    if (sectionId) {
      handleSave(data);
    } else {
      reset();
      close();
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    onSelectMenuItemImage,
    onSelectMenuItemOption,
    selectedMenuItemOptions,
    selectedMenuItemOptionsWithValues,
    handleChangePriceModifier,
    isLoadFetchMenuItem,
  };
};
